import ButtonLoader from "components/common/ButtonLoader";
import { CurrencySymbol } from "components/common/Utils";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const TapCheckout = ({
  isRedirectionEnabled,
  monthlyPrice,
  totalDue,
  handlePurchaseSubscription,
  isLoading,
  handleApplePay,
  paymentMethod,
}) => {
  const tapContainerRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const tapInstanceRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    // Cleanup function to prevent double initialization
    const cleanup = () => {
      if (tapInstanceRef.current) {
        try {
          tapInstanceRef.current.destroy();
        } catch (error) {
          console.log("Cleanup error:", error);
        }
        tapInstanceRef.current = null;
      }
    };

    const loadTapScript = () => {
      return new Promise((resolve, reject) => {
        if (document.getElementById("tap-script")) {
          return resolve();
        }

        const script = document.createElement("script");
        script.id = "tap-script";
        script.src = "https://secure.gosell.io/js/sdk/tap.min.js";
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const initTap = async () => {
      // Prevent double initialization
      if (isInitialized || tapInstanceRef.current) {
        console.log("Tap already initialized, skipping...");
        return;
      }

      try {
        await loadTapScript();

        if (!window.Tapjsli) {
          console.error("Tapjsli is not available.");
          return;
        }

        const tap = window.Tapjsli(process.env.REACT_APP_TAP_PUBLIC_KEY);
        const elements = tap.elements({});

        const style = {
          base: {
            color: "#535353",
            lineHeight: "18px",
            fontFamily: "sans-serif",
            fontSmoothing: "antialiased",
            fontSize: "16px",
            "::placeholder": {
              color: "rgba(0, 0, 0, 0.26)",
              fontSize: "15px",
            },
          },
          invalid: {
            color: "red",
          },
        };

        const labels = {
          cardNumber: t("Card Number"),
          expirationDate: t("MM/YY"),
          cvv: t("CVV"),
          cardHolder: t("Name on card"),
        };

        const paymentOptions = {
          currencyCode: ["KWD", "USD", "SAR"],
          amount: monthlyPrice || 1, // Use monthlyPrice for payment amount
          labels: labels,
          TextDirection: "ltr",
          paymentAllowed: ["VISA", "MASTERCARD", "AMERICAN_EXPRESS", "MADA"],
        };

        const card = elements.create("card", { style }, paymentOptions);

        // Clear container before mounting
        const container = document.getElementById("element-container");
        if (container) {
          container.innerHTML = "";
        }

        card.mount("#element-container");

        card.addEventListener("change", (event) => {
          const errorEl = document.getElementById("error-handler");
          if (event.error) {
            errorEl.textContent = event.error.message;
          } else {
            errorEl.textContent = "";
          }
        });

        // Handle form submission to get token
        const form = document.getElementById("form-container");
        if (form) {
          form.addEventListener("submit", async (event) => {
            event.preventDefault();
            setIsProcessing(true);
            try {
              const result = await tap.createToken(card);

              if (result.error) {
                console.error("Token creation error:", result.error);
                const errorEl = document.getElementById("error-handler");
                errorEl.textContent = result.error.message;
              } else {
                // CALL SUBSCRIPTION API
                handlePurchaseSubscription(result.id);
                console.log("Token ID:", result.id);

                // Display success message
                const successEl = document.getElementById("success");
                const tokenEl = document.getElementById("token");
                tokenEl.textContent = result.id;
                successEl.style.display = "block";
                setIsProcessing(true);
              }
            } catch (error) {
              console.error("Payment processing error:", error);
              const errorEl = document.getElementById("error-handler");
              // errorEl.textContent =
              //   "Payment processing failed. Please try again.";
            } finally {
              setIsProcessing(false);
            }
          });
        }

        // Store reference and mark as initialized
        tapInstanceRef.current = { tap, card, elements };
        setIsInitialized(true);
        console.log("Tap initialized successfully");
      } catch (error) {
        console.error("Tap integration error:", error);
        setIsInitialized(false);
      }
    };

    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initTap();
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [monthlyPrice, t]); // Include monthlyPrice and t in dependencies

  return (
    <div>
      <form id="form-container" method="post" action="/charge">
        <div id="element-container" ref={tapContainerRef}></div>
        <div id="error-handler" role="alert" style={{ color: "red" }}></div>
        {isInitialized && (
          <button
            id="tap-btn"
            type="submit"
            className="pay_btn"
            disabled={isRedirectionEnabled || isProcessing || (isLoading && paymentMethod === "CARD")}
          >
            {isRedirectionEnabled || isProcessing || (isLoading && paymentMethod === "CARD") ? (
              <ButtonLoader />
            ) : (
              <>
                <div className="d-flex gap-1 justify-content-center align-items-center">
                  <span>Pay</span>
                  <CurrencySymbol color="#fff" height="15" width="15" />
                  <span>{monthlyPrice || 1}</span>
                </div>
              </>
            )}
          </button>
        )}
      </form>
    </div>
  );
};

export default TapCheckout;